import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import RoleBasedNavigation from '../../components/ui/RoleBasedNavigation';
import { supplierService } from '../../services/supplierService';
import { useAuth } from '../../contexts/AuthContext';

import SupplierHeader from './components/SupplierHeader';
import LocationSection from './components/LocationSection';
import MenuSection from './components/MenuSection';
import Tabs from './components/Tabs';
import ChatModal from './components/ChatModal';
import Icon from '../../components/AppIcon';


const SupplierProfile = () => {
  const navigate = useNavigate();
  const { supplierId } = useParams();
  const [activeTab, setActiveTab] = useState('menu'); // Tab state: 'menu' (default) or 'location'
  const location = useLocation();
  const { isAuthenticated, user, loading: authLoading } = useAuth();
  const [isFavorite, setIsFavorite] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [supplierData, setSupplierData] = useState(null);
  const [menuItems, setMenuItems] = useState([]);
  const [isChatModalOpen, setIsChatModalOpen] = useState(false);

  // Fetch supplier data and menu items
  useEffect(() => {
    const fetchSupplierData = async () => {
      // Determine supplier source: URL param, navigation state, or current user's supplier
      const stateSupplierId = location?.state?.supplierId;
      const stateDishId = location?.state?.dishId;
      const effectiveSupplierId = supplierId || stateSupplierId;

      // If no supplier id in URL or state, wait for auth to finish loading and then try to resolve using logged-in user
      if (!effectiveSupplierId && !user?.id) {
        if (authLoading) {
          // Auth still loading; defer resolving supplier until auth finished
          return;
        }

        // No supplier ID and auth finished loading; this means we couldn't resolve the supplier
        setError('Supplier ID is required');
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        setError(null);

        let supplier = null;

        if (effectiveSupplierId) {
          const { data: s, error: supplierError } = await supplierService?.getSupplierById(effectiveSupplierId);
          if (supplierError) throw new Error(supplierError.message || 'Failed to fetch supplier data');
          supplier = s;
        } else if (user?.id) {
          // My Kitchen flow - fetch supplier record for logged-in user
          const { data: s, error: supplierError } = await supplierService?.getSupplierByUserId(user.id);
          if (supplierError) throw new Error(supplierError.message || 'Failed to fetch supplier data');
          supplier = s;
        }

        if (!supplier) throw new Error('Supplier not found');

        setSupplierData(supplier);

        // Fetch menu items
        const { data: items, error: menuError } = await supplierService?.getMenuItems(supplier?.id);
        if (menuError) throw new Error(menuError.message || 'Failed to fetch menu items');

        setMenuItems(items || []);

        // If navigated with a dish ID in state, scroll to or highlight it (handled in MenuSection if needed)
        if (stateDishId) {
          // Optionally, you could implement logic to auto-open dish details here.
          // For now we just log it for debugging purposes.
          console.debug('Opened supplier profile with dishId:', stateDishId);
        }
      } catch (err) {
        console.error('Error fetching supplier data:', err);
        setError(err?.message || 'Failed to load supplier profile');
      } finally {
        setLoading(false);
      }
    };

    fetchSupplierData();
  }, [supplierId, location?.state?.supplierId, user?.id]);

  const handleAddToCart = (item) => {
    console.log('Add to cart:', item);
    // TODO: Implement cart functionality
  };

  const handleCustomOrder = () => {
    console.log('Custom order requested');
    // TODO: Implement custom order functionality
  };

  const handleChatClick = () => {
    if (!isAuthenticated) {
      // Redirect to login if not authenticated
      navigate('/login', { state: { from: `/supplier-profile/${supplierId}` } });
      return;
    }
    setIsChatModalOpen(true);
  };

  const handleCloseChatModal = () => {
    setIsChatModalOpen(false);
  };

  // Loading state
  if (loading) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="flex justify-center items-center py-20">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>);

  }

  // Error state
  if (error || !supplierData) {
    return (
      <div className="min-h-screen bg-background">
        <RoleBasedNavigation />
        <div className="max-w-4xl mx-auto px-4 py-20 text-center">
          <div className="w-16 h-16 bg-destructive/10 rounded-full flex items-center justify-center mx-auto mb-4">
            <Icon name="AlertCircle" size={32} color="var(--color-destructive)" />
          </div>
          <h2 className="text-2xl font-heading font-bold mb-2">Supplier Not Found</h2>
          <p className="text-muted-foreground mb-6">{error || 'The supplier you are looking for does not exist.'}</p>
          <button
            onClick={() => navigate('/browse-kitchens')}
            className="px-6 py-3 bg-primary text-primary-foreground rounded-lg hover:bg-primary/90 transition-smooth">

            Browse All Kitchens
          </button>
        </div>
      </div>);

  }

  // Derive primary address and transform database data to component format
  const primaryAddress = (supplierData?.addresses || [])?.find(a => a?.is_primary) || (supplierData?.addresses || [])[0] || null;

  const locationObj = {
    address: primaryAddress ? [primaryAddress?.label, primaryAddress?.locality, primaryAddress?.pin_code].filter(Boolean).join(', ') : undefined,
    city: primaryAddress?.locality || undefined,
    state: primaryAddress?.state || undefined,
    zipCode: primaryAddress?.pin_code || undefined,
    country: primaryAddress?.country || supplierData?.country || undefined,
    phone: supplierData?.user?.phone || undefined,
    email: supplierData?.user?.email || undefined,
    serviceRadius: supplierData?.service_radius || 10,
    operatingHours: supplierData?.operating_hours || [],
    landmarks: supplierData?.landmarks || [],
    coordinates: primaryAddress?.latitude && primaryAddress?.longitude ? { lat: primaryAddress.latitude, lng: primaryAddress.longitude } : undefined,
    label: primaryAddress?.label
  };

  const transformedSupplierData = {
    id: supplierData?.id,
    name: supplierData?.business_name,
    tagline: supplierData?.service_description?.substring(0, 50) || 'Delicious homemade meals',
    profileImage: supplierData?.profile_image || 'https://images.unsplash.com/photo-1556910103-1c02745aae4d',
    profileImageAlt: supplierData?.profile_image_alt || `${supplierData?.business_name} profile`,
    coverImage: supplierData?.cover_image || "https://img.rocket.new/generatedImages/rocket_gen_img_1802096e4-1765031902527.png",
    coverImageAlt: `${supplierData?.business_name} kitchen`,
    rating: parseFloat(supplierData?.rating) || 0,
    reviewCount: supplierData?.total_reviews || 0,
    location: locationObj?.city ? `${locationObj.city}, ${locationObj.country || ''}`.trim() : (locationObj?.country || 'Location not specified'),
    responseTime: 'Usually responds in 2 hours',
    isVerified: supplierData?.is_verified || false,
    certifications: ['Food Safety Certified'],
    specialties: [],
    bio: supplierData?.service_description,
    stats: {
      totalOrders: supplierData?.total_orders || 0,
      repeatCustomers: 0,
      yearsExperience: 0,
      avgDeliveryTime: '45 min'
    },
    // For header: pass owner & edit handler
    isOwner: user?.id === supplierData?.user_id,
    onEditClick: () => navigate('/supplier-dashboard?editProfile=true'),
    // Keep raw supplier for deeper operations if needed
    raw: supplierData
  };

  // Transform menu items to component format
  const transformedMenuItems = menuItems?.map((item) => ({
    id: item?.id,
    name: item?.name,
    description: item?.description,
    image: item?.image || 'https://images.unsplash.com/photo-1546069901-ba9599a7e63c',
    imageAlt: item?.image_alt || item?.name,
    price: parseFloat(item?.price_per_plate),
    cuisine: item?.cuisine,
    dietary: item?.dietary_tags || [],
    rating: parseFloat(item?.rating) || 0,
    isAvailable: item?.is_available,
    preparationTime: item?.preparation_time,
    isNew: false,
    isPopular: parseFloat(item?.rating) >= 4.5
  }));



  const tabList = [
    { value: 'menu', label: 'Menu' },
    { value: 'location', label: 'Location & Service Area' }
  ];

  return (
    <div className="min-h-screen bg-background">
      <RoleBasedNavigation />

      <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-6 md:py-8 lg:py-10">
        <button
          onClick={() => navigate(-1)}
          className="flex items-center gap-2 text-muted-foreground hover:text-foreground transition-smooth mb-6">
          <Icon name="ArrowLeft" size={20} />
          <span className="text-sm md:text-base font-medium">Back to Search</span>
        </button>

        <div className="space-y-6 md:space-y-8">
          <SupplierHeader
            supplier={transformedSupplierData}
            onChatClick={handleChatClick}
            onFavoriteToggle={() => setIsFavorite(!isFavorite)}
            isFavorite={isFavorite} />

          <Tabs tabs={tabList} activeTab={activeTab} onTabChange={setActiveTab} />

          {activeTab === 'menu' && (
            <MenuSection
              menuItems={transformedMenuItems}
              onAddToCart={handleAddToCart}
              onCustomOrder={handleCustomOrder}
            />
          )}
          {activeTab === 'location' && (
            <LocationSection location={locationObj} />
          )}
        </div>
      </div>

      <ChatModal
        isOpen={isChatModalOpen}
        onClose={handleCloseChatModal}
        supplier={transformedSupplierData}
      />

      <footer className="bg-card border-t border-border mt-12 md:mt-16 lg:mt-20">
        <div className="max-w-7xl mx-auto px-4 md:px-6 lg:px-8 py-8 md:py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">About HungryElph</h3>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Connecting food lovers with talented home chefs for authentic, homemade meals.
              </p>
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Quick Links</h3>
              <div className="space-y-2">
                <button className="block text-sm text-muted-foreground hover:text-foreground transition-smooth">
                  Browse Kitchens
                </button>
                <button className="block text-sm text-muted-foreground hover:text-foreground transition-smooth">
                  Become a Chef
                </button>
                <button className="block text-sm text-muted-foreground hover:text-foreground transition-smooth">
                  Help Center
                </button>
              </div>
            </div>
            <div>
              <h3 className="font-heading font-bold text-lg mb-4">Contact</h3>
              <div className="space-y-2">
                <p className="text-sm text-muted-foreground">support@hungryelph.com</p>
                <p className="text-sm text-muted-foreground">1-800-KITCHEN</p>
              </div>
            </div>
          </div>
          <div className="mt-8 pt-8 border-t border-border text-center">
            <p className="text-sm text-muted-foreground">
              © {new Date()?.getFullYear()} HungryElph. All rights reserved.
            </p>
          </div>
        </div>
      </footer>
    </div>);

};

export default SupplierProfile;